'use strict';

var d3 = require('d3');
var _ = require('lodash');

/**
 * HoverOver
 *
 * A moving line with circles on each line chart. Shows tooltip when mouse is over the circle
 *
 * @param tooltip
 * @returns {Function}
 * @constructor
 */
var HoverOver = function HoverOver(tooltip, $document, competitorFaresTooltipService) {
  return function (lineDataLocation, xPropertyName, yPropertyName, tooltipFormatter, interpolate, tooltipClass, filterIsMatched) {
    var scales;
    var params;
    var data;
    var tip = tooltip(tooltipFormatter, tooltipClass);
    var focus;
    var mousedown = false;

    /**
     * Constructs focus line DOM element
     * @param container
     */
    function addFocus(container) {
      container.selectAll('g.focus').remove();
      focus = container.append('g').attr('class', 'focus').style('display', 'none');
      focus.append('line').attr('class', 'x-focus-line').attr('y1', 0).attr('y2', 1);
    }

    /**
     * Constructs overlay DOM element - to catch mouse events
     * @param container
     */
    function addOverlay(container) {
      container.selectAll('rect.overlay').remove();
      $document.on('mouseup', unsetMousedown);
      container.append('rect').attr('class', 'overlay').attr('width', params.width).attr('height', params.height).on('mouseout', function () {
        toggleMousedown(false);
        hoverOver.hideFocus();
      }).on('mousedown', function () {
        toggleMousedown(true);
        hoverOver.hideFocus();
      }).on('mouseup', function () {
        toggleMousedown(false);
      }).on('mousemove', function () {
        var mouseCoordinateX = d3.mouse(this)[0];
        hoverOver.moveFocus(mouseCoordinateX);
      });
    }
    function toggleMousedown(updateMouseDown) {
      mousedown = updateMouseDown;
    }
    function unsetMousedown() {
      toggleMousedown(false);
    }

    /**
     * Finds the closest point in chart data for the given X value
     * @param array
     * @param value
     * @param propertyName
     * @returns {*}
     */
    function findClosestPoint(array, value) {
      var tmpArray = _.filter(array, function (item) {
        return item[yPropertyName] !== undefined;
      });
      var result = _.find(tmpArray, _.set({}, xPropertyName, value));
      if (!result) {
        var closestIndex = 0;
        var diff = _.last(tmpArray)[xPropertyName];
        _.forEachRight(tmpArray, function (item, index) {
          var newDiff = Math.abs(value - item[xPropertyName]);
          if (newDiff < diff && item[yPropertyName] !== undefined) {
            diff = newDiff;
            closestIndex = index;
          } else {
            return false;
          }
        });
        return tmpArray[closestIndex];
      }
      return result;
    }

    /**
     * Finds X scale data point
     * @param {number} x
     * @returns {*}
     */
    function findDataPoints(x) {
      return _.chain(data).map(function (el) {
        var result = _.omit(el, lineDataLocation);
        var pointData = interpolate ? interpolate(el, x) : findClosestPoint(el[lineDataLocation], x);
        _.assign(result, pointData);
        return result;
      }).compact().sortBy(function (item) {
        return Math.abs(x - item[xPropertyName]);
      }).value();
    }

    /**
     * Sets X position for line
     * @param translateX
     */
    function moveLine(translateX) {
      focus.select('.x-focus-line').attr({
        'transform': 'translate(' + translateX + ', 0)',
        'y2': params.height
      });
      focus.style('display', null);
    }
    function calcTooltipWidth(data) {
      var longestFlightList = 0;
      if (filterIsMatched()) {
        longestFlightList = _.reduce(data, function (d, n) {
          return Math.max(d, n.fltNbrList ? n.fltNbrList.length : 0);
        }, 0);
      }
      var flightColWidth = longestFlightList > 1 ? longestFlightList * 7 : 60;
      var tooltipWidth = 230 + flightColWidth;
      var numColumns = competitorFaresTooltipService.splitDataToColumns(data).length;
      return tooltipWidth * numColumns;
    }
    function alignTooltipPosition(align) {
      if (align === 'center') {
        tip.direction('n');
        tip.offset([-18, 0]);
      } else if (align === 'left') {
        tip.direction('ne');
        tip.offset([-18, -(event.clientX - 2)]);
      } else if (align === 'right') {
        tip.direction('nw');
        tip.offset([-18, window.innerWidth - (event.clientX - 2)]);
      }
    }
    function updateTooltipPosition(data) {
      if (data.length > 0) {
        var toolTipWidth = calcTooltipWidth(data);
        var halfWidth = toolTipWidth * 0.5;
        if (window.innerWidth > toolTipWidth) {
          if (event.clientX > halfWidth) {
            if (window.innerWidth - event.clientX > halfWidth) {
              alignTooltipPosition('center');
            } else {
              alignTooltipPosition('right');
            }
          } else {
            alignTooltipPosition('left');
          }
        } else {
          if (event.clientX < window.innerWidth * 0.45) {
            alignTooltipPosition('left');
          } else if (event.clientX > window.innerWidth * 0.55) {
            alignTooltipPosition('right');
          } else {
            alignTooltipPosition('center');
          }
        }
      }
    }
    function showTooltip(data) {
      var rootElement = focus.select('.x-focus-line')[0][0];
      updateTooltipPosition(data);
      tip.show(data, rootElement);
    }

    /**
     * Sets X and Y position for circles
     * @param data
     */
    function moveCircles(data) {
      var circles = focus.selectAll('.focus-circle').data(data);
      circles.enter().append('circle').attr('class', 'focus-circle');
      circles.exit().remove();
      circles.attr({
        cx: function cx(d) {
          return scales.xScale(d[xPropertyName]);
        },
        cy: function cy(d) {
          return scales.yScale(d[yPropertyName]);
        },
        r: 4,
        fill: 'none',
        stroke: function stroke(d) {
          return d.fill;
        }
      });
    }

    /**
     *
     * @param container
     */
    var hoverOver = function hoverOver(container) {
      data = container.datum();
      container.call(tip);
      addOverlay(container);
      addFocus(container);
    };

    /**
     * Hides focus group
     */
    hoverOver.hideFocus = function () {
      tip.hide();
      tip.style({
        top: '0px',
        left: '0px'
      });
      focus.style('display', 'none');
    };

    /**
     * Handler for mouse move event
     * @param x
     */
    hoverOver.moveFocus = function (x) {
      tip.hide();
      var xValue = Math.round(scales.xScale.invert(x));
      var data = findDataPoints(xValue);
      var closestPoint = data[0];
      var filteredData = _.filter(data, function (item) {
        return item[xPropertyName] === closestPoint[xPropertyName];
      });
      var xValueScaled = scales.xScale(closestPoint[xPropertyName]);
      var visibleRange = scales.xScale.range();
      moveLine(xValueScaled);
      moveCircles(filteredData);
      if (tooltipInRange(xValueScaled, visibleRange) && !mousedown) {
        showTooltip(filteredData);
      }
    };
    function tooltipInRange(value, range) {
      return value >= range[0] && value <= range[1] || value >= range[1] && value <= range[0];
    }

    /**
     * Sets/gets hover's params
     * @param value
     * @returns {hoverOver}
     */
    hoverOver.params = function (value) {
      params = value;
      return hoverOver;
    };

    /**
     * Sets/gets hover's scales
     * @param value
     * @returns {hoverOver}
     */
    hoverOver.scales = function (value) {
      scales = value;
      return hoverOver;
    };

    /**
     * Sets the tooltip formatter
     * @param value
     * @returns {hoverOver}
     */
    hoverOver.withTooltipFormatter = function (formatter, cssClass) {
      tip = tooltip(formatter, cssClass || tooltipClass);
      return hoverOver;
    };
    hoverOver.destroy = function () {
      $document.off('mouseup', unsetMousedown);
    };
    return hoverOver;
  };
};
HoverOver.$inject = ['CompetitorFaresChartTooltip', '$document', 'CompetitorFaresTooltipService'];
module.exports = HoverOver;